"use client";

import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { Button } from "@/components/ui/button";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface DuplicateSessionDialogProps {
  onClose: () => void;
}

export function DuplicateSessionDialog({ onClose }: DuplicateSessionDialogProps) {
  const { t } = useTranslation(["classroom", "common"]);

  return (
    <DialogContent className="bg-side-accent/80 text-accent-foreground max-w-md overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="from-accent-foreground/40 to-accent-foreground/60 absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br" />
        <div className="from-accent-foreground/30 to-accent-foreground/50 absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr" />
      </div>

      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="relative p-6"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        <DialogHeader className="mb-6">
          <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
            <div className="flex items-center justify-center rounded-xl bg-amber-500/20 p-3 backdrop-blur-sm">
              <Icon
                className="text-amber-500"
                height="1.5rem"
                icon="lucide:user-check"
                width="1.5rem"
              />
            </div>
            {t("classroom:duplicateSessionDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          transition={{ delay: 0.2 }}
        >
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t("classroom:duplicateSessionDialog.description")}
          </p>
        </motion.div>

        <DialogFooter>
          <Button
            className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 w-full rounded-xl"
            onClick={onClose}
          >
            {t("classroom:duplicateSessionDialog.okay")}
          </Button>
        </DialogFooter>
      </motion.div>
    </DialogContent>
  );
}
